/// <reference lib="dom" />
/**
 * Browser audio for voice turns (R8.1).
 *
 * BrowserMicrophone (MicrophoneCapture): captures the microphone through an
 * AudioWorklet, downsamples to 16 kHz mono, converts to 16-bit little-endian
 * PCM (Nova Sonic's input format), and emits ~200 ms chunks.
 *
 * PcmPlayer (AudioSink): plays the agent's 24 kHz 16-bit mono PCM prompt audio
 * gaplessly by scheduling each chunk right after the previous one, and can be
 * stopped instantly when the user starts speaking (barge-in).
 *
 * The PCM conversion helpers are pure so they are unit-testable without a
 * browser audio stack.
 */
import { AudioSink, MicrophoneCapture } from './voice-controller';

export const CAPTURE_RATE = 16_000;
/** 200 ms of 16 kHz 16-bit mono audio. */
const CHUNK_BYTES = (CAPTURE_RATE * 2) / 5;

/** Average-downsample float samples from `fromRate` to `toRate`. */
export function downsample(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (toRate >= fromRate) return input;
  const ratio = fromRate / toRate;
  const out = new Float32Array(Math.floor(input.length / ratio));
  for (let i = 0; i < out.length; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(Math.floor((i + 1) * ratio), input.length);
    let sum = 0;
    for (let j = start; j < end; j++) sum += input[j];
    out[i] = end > start ? sum / (end - start) : 0;
  }
  return out;
}

/** Float samples in [-1, 1] -> 16-bit little-endian PCM bytes. */
export function floatToPcm16(samples: Float32Array): Uint8Array {
  const out = new Uint8Array(samples.length * 2);
  const view = new DataView(out.buffer);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return out;
}

/** 16-bit little-endian PCM bytes -> float samples in [-1, 1]. */
export function pcm16ToFloat(bytes: Uint8Array): Float32Array {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const out = new Float32Array(Math.floor(bytes.byteLength / 2));
  for (let i = 0; i < out.length; i++) out[i] = view.getInt16(i * 2, true) / 0x8000;
  return out;
}

// Forwards each 128-frame render quantum (channel 0) to the main thread.
const WORKLET_SOURCE = `
class CaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) this.port.postMessage(ch.slice(0));
    return true;
  }
}
registerProcessor('txhhsc-capture', CaptureProcessor);
`;

/** Normalized RMS level of float samples in [-1, 1]. */
export function rms(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (const s of samples) sum += s * s;
  return Math.sqrt(sum / samples.length);
}

export type SilenceState = 'waiting' | 'speaking' | 'paused' | 'end' | 'no_speech' | 'max';

export interface SilenceDetectorOptions {
  /** Silence after speech that ends the answer. */
  endSilenceMs?: number;
  /** Background noise is measured over this initial window. */
  calibrationMs?: number;
  /** Give up if nobody speaks within this time. */
  noSpeechMs?: number;
  /** Longest capture. */
  maxMs?: number;
}

/**
 * Detects the end of a spoken answer from microphone levels (buffered voice
 * mode; in live mode the agent detects the end itself). It measures the
 * background noise at the start, treats anything well above it as speech
 * (the threshold is capped so talking straight away still counts), and ends
 * once there has been `endSilenceMs` of silence after some speech. Short
 * pauses between sentences do not end the answer.
 */
export class SilenceDetector {
  private readonly o: Required<SilenceDetectorOptions>;
  private startedAt = -1;
  private floor = Infinity;
  private threshold = 0.012;
  private lastVoiceAt = -1;

  constructor(opts: SilenceDetectorOptions = {}) {
    this.o = {
      endSilenceMs: opts.endSilenceMs ?? 2_000,
      calibrationMs: opts.calibrationMs ?? 400,
      noSpeechMs: opts.noSpeechMs ?? 10_000,
      maxMs: opts.maxMs ?? 60_000,
    };
  }

  /** Feed one level reading; returns the capture state at `nowMs`. */
  feed(level: number, nowMs: number): SilenceState {
    if (this.startedAt < 0) this.startedAt = nowMs;
    const elapsed = nowMs - this.startedAt;
    if (elapsed < this.o.calibrationMs) {
      this.floor = Math.min(this.floor, level);
      // Capped (0.03) so speech during calibration cannot hide soft syllables.
      this.threshold = Math.min(0.03, Math.max(0.012, this.floor * 3));
    }
    if (level >= this.threshold) this.lastVoiceAt = nowMs;
    if (elapsed >= this.o.maxMs) return 'max';
    if (this.lastVoiceAt < 0) return elapsed >= this.o.noSpeechMs ? 'no_speech' : 'waiting';
    const silentFor = nowMs - this.lastVoiceAt;
    if (silentFor >= this.o.endSilenceMs) return 'end';
    return silentFor > 300 ? 'paused' : 'speaking';
  }
}

export class BrowserMicrophone implements MicrophoneCapture {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private node: AudioWorkletNode | null = null;
  private pending: Uint8Array[] = [];
  private pendingBytes = 0;
  private onChunk: ((chunk: Uint8Array) => void) | null = null;

  /** @param onLevel called with the RMS level of each audio block (~3 ms). */
  constructor(private readonly onLevel?: (level: number) => void) {}

  async start(onChunk: (chunk: Uint8Array) => void): Promise<void> {
    this.onChunk = onChunk;
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    this.ctx = new AudioContext();
    const url = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
    try {
      await this.ctx.audioWorklet.addModule(url);
    } finally {
      URL.revokeObjectURL(url);
    }
    const source = this.ctx.createMediaStreamSource(this.stream);
    this.node = new AudioWorkletNode(this.ctx, 'txhhsc-capture');
    const rate = this.ctx.sampleRate;
    this.node.port.onmessage = (ev: MessageEvent<Float32Array>) => {
      this.onLevel?.(rms(ev.data));
      this.push(floatToPcm16(downsample(ev.data, rate, CAPTURE_RATE)));
    };
    source.connect(this.node);
  }

  private push(pcm: Uint8Array): void {
    this.pending.push(pcm);
    this.pendingBytes += pcm.length;
    if (this.pendingBytes >= CHUNK_BYTES) this.flush();
  }

  private flush(): void {
    if (this.pendingBytes === 0) return;
    const chunk = new Uint8Array(this.pendingBytes);
    let offset = 0;
    for (const p of this.pending) {
      chunk.set(p, offset);
      offset += p.length;
    }
    this.pending = [];
    this.pendingBytes = 0;
    this.onChunk?.(chunk);
  }

  async stop(): Promise<void> {
    this.node?.port.close();
    this.node?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    await this.ctx?.close();
    this.flush();
    this.node = null;
    this.stream = null;
    this.ctx = null;
    this.onChunk = null;
  }
}

export class PcmPlayer implements AudioSink {
  private ctx: AudioContext | null = null;
  private nextStart = 0;
  private sources = new Set<AudioBufferSourceNode>();

  constructor(private readonly sampleRate = 24_000) {}

  /** Create or resume the context; call from a user gesture so playback is allowed. */
  unlock(): void {
    if (!this.ctx) this.ctx = new AudioContext();
    void this.ctx.resume();
  }

  async play(pcm: Uint8Array): Promise<void> {
    this.unlock();
    const ctx = this.ctx!;
    const samples = pcm16ToFloat(pcm);
    if (samples.length === 0) return;
    const buffer = ctx.createBuffer(1, samples.length, this.sampleRate);
    buffer.copyToChannel(samples, 0);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    const startAt = Math.max(ctx.currentTime + 0.02, this.nextStart);
    src.start(startAt);
    this.nextStart = startAt + buffer.duration;
    this.sources.add(src);
    src.onended = () => this.sources.delete(src);
  }

  /** Stop all queued and playing audio (barge-in). */
  stop(): void {
    for (const s of this.sources) {
      try {
        s.stop();
      } catch {
        // already stopped
      }
    }
    this.sources.clear();
    this.nextStart = 0;
  }
}
